/**
 * The copy law: what an operator is allowed to read on this console.
 *
 * A venue operator runs a room. They did not build the rig, they do not know that the room's geometry
 * travels as a published artifact, and telling them so is worse than telling them nothing — it names a
 * thing they cannot act on and asks them to reason about our storage layer to turn a light on. The rule
 * the owner set (2026-09-14) is absolute: operator-facing text never contains our internals.
 *
 * Everything on the forbidden list below is an INTERNAL noun. When one of them is the honest reason a
 * control cannot be used, the reason does not disappear — it moves. The operator reads one sentence
 * (`effectUnavailableMessage`), and the raw reason travels beside it as `detail`, shown only to
 * owner/manager/staff behind the "Details" disclosure (`TechnicalDetails` in `components/ui.tsx`).
 *
 * `operatorCopy.test.ts` enforces this over the whole source tree: every JSX text node, every
 * user-visible JSX attribute and every copy table is scanned, and a new violation fails the build.
 * Legitimate exceptions live in `operatorCopy.allow.ts`, each with a written rationale.
 */

/**
 * One forbidden internal term. `pattern` must be word-bounded — `\brig\b` so that "brightness" is not a
 * violation — and `term` is what the failure message names.
 */
export interface ForbiddenTerm {
  term: string;
  pattern: RegExp;
}

/**
 * The owner's list, verbatim, compiled. Order matters only for which term is reported first.
 *
 * Two deliberate narrowings, so the rule catches internals without flagging plain English:
 * - `push` matches only the bare verb forms, so "the pulse bunches up before pushing through" is fine
 *   while "push the rig again" is not.
 * - `Bluetooth` is NOT forbidden: an operator really does pair a controller over Bluetooth, and it is
 *   their word for it. `BLE` — our word for the same thing — is forbidden.
 */
export const FORBIDDEN_OPERATOR_TERMS: readonly ForbiddenTerm[] = [
  { term: 'map', pattern: /\bmaps?\b/i },
  { term: 'layout', pattern: /\blayouts?\b/i },
  { term: 'rig', pattern: /\brigs?\b/i },
  { term: 'version', pattern: /\bversions?\b/i },
  { term: 'publish', pattern: /\bpublish(es|ed|ing)?\b/i },
  { term: 'push', pattern: /\bpush(ed|es)?\b/i },
  { term: 'artifact', pattern: /\bartifacts?\b/i },
  { term: 'schematic', pattern: /\bschematics?\b/i },
  { term: 'continuous run', pattern: /\bcontinuous runs?\b/i },
  { term: 'pathIndex', pattern: /\bpath ?index\b/i },
  { term: 'element', pattern: /\belements?\b/i },
  { term: 'universe', pattern: /\buniverses?\b/i },
  { term: 'mirror', pattern: /\bmirror(s|ed|ing)?\b/i },
  { term: 'relay', pattern: /\brelay(s|ed|ing)?\b/i },
  { term: 'catalog', pattern: /\bcatalogu?e?s?\b/i },
  { term: 'digest', pattern: /\bdigests?\b/i },
  { term: 'nudge', pattern: /\bnudg(e|es|ed|ing)\b/i },
  { term: 'daemon', pattern: /\bdaemons?\b/i },
  { term: 'sidecar', pattern: /\bsidecars?\b/i },
  { term: 'ALSA', pattern: /\bALSA\b/ },
  { term: 'hw:', pattern: /\bhw:\d/i },
  { term: 'protocol', pattern: /\bprotocols?\b/i },
  { term: 'WebRTC', pattern: /\bWebRTC\b/i },
  { term: 'TURN', pattern: /\bTURN\b/ },
  { term: 'transport', pattern: /\btransports?\b/i },
  { term: 'LAN', pattern: /\bLAN\b/ },
  { term: 'BLE', pattern: /\bBLE\b/ },
  { term: 'id', pattern: /\bids?\b/i },
  // An id-SHAPED token: `ven_pN_nMaNE`, `effect-4da1c0ac`, `sha256:…`, a bare uuid. The console has
  // rendered every one of these beside a name at some point; none of them ever belongs on screen.
  { term: 'an identifier', pattern: /\b(?:[a-z]{2,6}_[A-Za-z0-9_-]{6,}|sha256:|[0-9a-f]{8}-[0-9a-f]{4})/ },
];

/**
 * Every forbidden term in `text`, in list order. Empty means the string is safe to show an operator.
 *
 * This is the whole law as a function, so a future surface — the generic manifest-driven effect panel
 * included — can check a string it assembles at runtime rather than waiting for the test to catch it.
 */
export function operatorCopyViolations(text: string): string[] {
  return FORBIDDEN_OPERATOR_TERMS.filter((entry) => entry.pattern.test(text)).map((entry) => entry.term);
}

/** True when `text` can be shown to a venue operator. */
export function isOperatorCopy(text: string): boolean {
  return operatorCopyViolations(text).length === 0;
}

/**
 * The one sentence an operator reads when an effect cannot be started in this room — whatever the
 * reason. "Yet" is doing real work: every cause is something staff can put right, so the sentence is
 * true for a rig that never carried the room's geometry, for a download that failed, and for a console
 * on a cable with no way to reach it.
 */
export function effectUnavailableMessage(effectName: string): string {
  return `${effectName} isn't available for this room yet.`;
}

/**
 * What an operator is told when the room's setup changed under them. It is the same event whether the
 * console noticed it on its own or discovered it when a start was refused, so it is one string in one
 * place.
 */
export const ROOM_UPDATED_NOTICE = 'Room updated';
